// LIVE-SELLER-RECORDING-TEST-FLOW-1 · 방송 녹화 설정 · 서버 녹화 상태 · 연결 상품별 READY 영상 · 소비자 화면 테스트 진입.
// REUSE: PUT /seller/broadcasts/{id}/recording · GET /seller/broadcasts/{id}/recording-overview · GET /seller/product-videos/{seg}/play-url
import React, { useCallback, useEffect, useState } from 'react';
import { Film, Play, X, ExternalLink, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { autoGenLabel } from '@/lib/autoGenState';

const TEST_ENTRY = process.env.REACT_APP_STUDIO_TEST_ENTRY === '1';
const ERR = { already_live: 'LIVE 진행 중에는 녹화 설정을 바꿀 수 없어요.', schedule_ended: '종료·취소된 방송은 변경할 수 없어요.' };

export const recordingStatusLabel = (ov) => {
  if (!ov) return { label: '확인 중', tone: 'gray' };
  const st = ov.session?.recording?.status;
  const live = ov.session?.streaming_status === 'live';
  if (st === 'STARTING') return { label: '녹화 준비', tone: 'amber' };
  if (st === 'RECORDING') return { label: '녹화 중', tone: 'red' };
  if (st === 'STOPPING') return { label: '녹화 종료 · 저장 중', tone: 'amber' };
  if (st === 'COMPLETED') return { label: '저장 완료', tone: 'green' };
  if (st === 'FAILED' || st === 'INTERRUPTED') return { label: '녹화 실패', tone: 'red' };
  if (!ov.recording_enabled) return { label: '녹화 안 함', tone: 'gray' };
  return { label: live ? '녹화 시작 안 됨' : '녹화 대기 · 방송 시작 시 녹화', tone: 'gray' };
};

const TONE = { gray: 'bg-gray-100 text-gray-700', amber: 'bg-amber-100 text-amber-800', red: 'bg-red-100 text-red-700', green: 'bg-emerald-100 text-emerald-700' };

export const RecordingSettingsFields = ({ value, onChange, disabled, reason }) => (
  <div className="space-y-2" data-testid="rec-settings">
    <p className="text-sm font-bold">방송 녹화</p>
    {[{ k: false, l: '녹화 안 함' }, { k: true, l: '녹화하기' }].map((o) => (
      <label key={String(o.k)} className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="radio" name="rec-enabled" checked={value.enabled === o.k} disabled={disabled}
               onChange={() => onChange({ ...value, enabled: o.k })} data-testid={`rec-enabled-${o.k ? 'on' : 'off'}`} />
        {o.l}
      </label>
    ))}
    {value.enabled && (
      <div className="ml-5 space-y-2 border-l-2 border-black/10 pl-3" data-testid="rec-mode-group">
        <p className="text-xs font-bold text-gray-500">녹화 방식</p>
        {[{ k: 'clean_participant', l: '클린 녹화', d: '카메라 영상 + 목소리 저장 · 상품명·가격 등 방송 오버레이 제외' },
          { k: 'composite', l: '방송 화면 그대로 녹화', d: '기존 방식 · 오버레이 포함 화면 저장' }].map((o) => (
          <label key={o.k} className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="radio" name="rec-mode" className="mt-1" checked={value.mode === o.k} disabled={disabled}
                   onChange={() => onChange({ ...value, mode: o.k })} data-testid={`rec-mode-${o.k}`} />
            <span><b>{o.l}</b><span className="block text-[11px] text-gray-500">{o.d}</span></span>
          </label>
        ))}
        {value.mode === 'clean_participant' && (
          <p className="text-[11px] text-amber-700 flex gap-1" data-testid="rec-clean-simulcast-note">
            <AlertTriangle size={12} className="shrink-0 mt-0.5" /> 클린 녹화는 외부 동시송출(YouTube·Instagram)과 함께 지원되지 않아요. 외부 송출을 켜면 녹화가 실패할 수 있어요.
          </p>
        )}
        <label className="flex items-start gap-2 text-sm cursor-pointer pt-1">
          <input type="checkbox" className="mt-1" checked={value.auto !== false} disabled={disabled}
                 onChange={(e) => onChange({ ...value, auto: e.target.checked })} data-testid="rec-auto-split" />
          <span><b>상품 소개영상 자동 생성</b>
            <span className="block text-[11px] text-gray-500">방송을 종료하고 녹화 저장이 완료되면 소개한 상품별 영상이 자동으로 생성됩니다. 첫 번째 소개영상이 기본 대표영상이 되며, 나중에 변경할 수 있습니다.</span>
          </span>
        </label>
        <p className="text-[11px] text-gray-500" data-testid="rec-cost-note">녹화를 켜면 영상 저장·처리 비용이 발생할 수 있어요. 설정만으로 녹화가 시작되지 않고, LIVE 시작 시 녹화돼요.</p>
      </div>
    )}
    {reason && <p className="text-[11px] text-gray-500" data-testid="rec-locked-reason">{reason}</p>}
  </div>
);

export const saveRecording = async (scheduleId, value) => {
  try {
    await api.put(`/seller/broadcasts/${scheduleId}/recording`, { enabled: !!value.enabled, mode: value.mode, auto_split: value.auto !== false });
    return true;
  } catch (e) {
    const d = e?.response?.data?.detail;
    toast.error(ERR[d] || d || '녹화 설정 저장 실패');
    return false;
  }
};

const PreviewModal = ({ url, name, onClose }) => (
  <div className="fixed inset-0 z-[400] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="rec-preview-modal">
    <div className="relative w-full max-w-[320px]" onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={onClose} className="absolute -top-9 right-0 text-white p-1" data-testid="rec-preview-close"><X size={20} /></button>
      <p className="text-white text-xs font-bold mb-1 truncate">{name}</p>
      <video src={url} controls autoPlay playsInline className="w-full aspect-[9/16] bg-black rounded-lg" data-testid="rec-preview-video" />
    </div>
  </div>
);

const ProductVideoRow = ({ p, onPreview }) => (
  <li className="flex items-center gap-2 py-2" data-testid={`rec-product-${p.product_id}`}>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-bold truncate">{p.name}</p>
      {p.video ? (
        <p className="text-[11px] text-emerald-700 font-bold" data-testid={`rec-product-ready-${p.product_id}`}>
          {p.video.source === 'clean' ? '클린 영상 READY' : '방송화면 영상 READY'} · 소비자 영상 보기 사용
        </p>
      ) : <p className="text-[11px] text-gray-500" data-testid={`rec-product-none-${p.product_id}`}>저장된 소개영상 없음</p>}
    </div>
    {p.video && (
      <button type="button" onClick={() => onPreview(p)} className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-bold"
              data-testid={`rec-product-preview-${p.product_id}`}>
        <Play size={11} /> 영상 미리보기
      </button>
    )}
  </li>
);

export const RecordingOverview = ({ scheduleId, reloadKey = 0 }) => {
  const [ov, setOv] = useState(null);
  const [preview, setPreview] = useState(null);
  const load = useCallback(() => {
    api.get(`/seller/broadcasts/${scheduleId}/recording-overview`).then((r) => setOv(r.data)).catch(() => setOv(null));
  }, [scheduleId]);
  useEffect(() => { if (scheduleId) load(); }, [scheduleId, reloadKey, load]);
  const openPreview = async (p) => {
    try {
      const { data } = await api.get(`/seller/product-videos/${p.video.segment_id}/play-url`);
      setPreview({ url: data.url, name: p.name });
    } catch { toast.error('영상을 불러오지 못했어요.'); }
  };
  if (!ov) return null;
  const st = recordingStatusLabel(ov);
  return (
    <div className="space-y-2 text-sm" data-testid="rec-overview">
      <div className="flex flex-wrap items-center gap-1.5">
        <Film size={14} />
        <span className="font-bold">방송 녹화: {ov.recording_enabled ? 'ON' : 'OFF'}</span>
        {ov.recording_enabled && <span className="text-xs text-gray-500">· {ov.recording_mode === 'clean_participant' ? '클린 영상' : '방송 화면 그대로'}</span>}
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${TONE[st.tone]}`} data-testid="rec-status">{st.label}</span>
      </div>
      {autoGenLabel(ov) && (
        <p className={`text-[11px] px-2 py-1 rounded ${TONE[autoGenLabel(ov).tone]}`} data-testid="rec-split-note">
          상품영상: {autoGenLabel(ov).text}
          {!ov.storage_configured && ' · 영상 저장소 미설정'}
        </p>
      )}
      <div>
        <p className="text-xs font-bold text-gray-500">연결 상품 {ov.products.length}개 · 소개영상</p>
        {ov.products.length === 0 ? <p className="text-[11px] text-gray-500 py-1">연결된 상품이 없어요.</p> : (
          <ul className="divide-y divide-black/5">{ov.products.map((p) => <ProductVideoRow key={p.product_id} p={p} onPreview={openPreview} />)}</ul>
        )}
      </div>
      {TEST_ENTRY && ov.test_viewer_enabled && (
        <a href={`/live-view/__TEST_SESSION__?test_schedule=${scheduleId}`} target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-black border border-amber-300"
           data-testid="rec-test-viewer-link">
          <ExternalLink size={12} /> 소비자 화면 테스트 (TEST · LIVE 아님)
        </a>
      )}
      {preview && <PreviewModal {...preview} onClose={() => setPreview(null)} />}
    </div>
  );
};

export const BroadcastRecordingModal = ({ broadcast, onClose, onSaved }) => {
  const [val, setVal] = useState({ enabled: !!broadcast.recording_enabled, mode: broadcast.recording_mode || 'composite', auto: broadcast.recording_auto_split !== false });
  const [busy, setBusy] = useState(false);
  const [rk, setRk] = useState(0);
  const locked = ['종료', 'ended', '취소', 'cancelled'].includes(broadcast.status);
  const save = async () => {
    setBusy(true);
    if (await saveRecording(broadcast.schedule_id, val)) { toast.success('녹화 설정이 저장되었습니다.'); setRk((k) => k + 1); onSaved?.(); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div className="w-full max-w-lg bg-white dark:bg-[#141414] rounded-2xl p-5 max-h-[90vh] overflow-y-auto space-y-4" onClick={(e) => e.stopPropagation()} data-testid="rec-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-lg truncate">녹화 · 상품영상 · {broadcast.title}</h3>
          <button type="button" onClick={onClose} className="p-1" data-testid="rec-modal-close"><X size={16} /></button>
        </div>
        <RecordingSettingsFields value={val} onChange={setVal} disabled={locked || busy} reason={locked ? '종료·취소된 방송은 녹화 설정을 바꿀 수 없어요.' : 'LIVE 시작 후에는 녹화 설정을 바꿀 수 없어요.'} />
        {!locked && (
          <button type="button" onClick={save} disabled={busy} className="px-4 py-2 rounded-full bg-[#FF3B30] text-white text-sm font-bold disabled:opacity-60" data-testid="rec-save">
            {busy ? '저장 중...' : '녹화 설정 저장'}
          </button>
        )}
        <div className="border-t pt-3"><RecordingOverview scheduleId={broadcast.schedule_id} reloadKey={rk} /></div>
        <a href={`/seller/dashboard?section=videos&schedule=${encodeURIComponent(broadcast.schedule_id)}`} className="inline-flex items-center gap-1 text-xs font-bold text-[#FF3B30]" data-testid="rec-modal-open-library">
          <Film size={12} /> 녹화·영상 관리에서 전체 녹화 보기
        </a>
      </div>
    </div>
  );
};

export default BroadcastRecordingModal;
